// @flow
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import { FormValidator } from '../../../../../utils/form/index';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import Loading from '../../../../../elements/Loading/Loading';
import Modal from '../../../../../elements/Modal/Modal';

import style from './AlreadyExistsMailAccount.scss';
import buttonStyle from '../../../../../assets/styles/button.scss';
import inputStyle from '../../../../../assets/styles/input.scss';
import formStyle from '../../../../../assets/styles/form.scss';

import type { HTTPError } from '../../../../../error';
import type { FormValidationResult } from '../../../../../utils/form';

type Props = {
  +handleClose: () => void;
  +login: (email: string, password: string, redirectTo?: string) => void;
  +isAuthing: ?boolean;
  +isOpen?: boolean;
  +authError?: HTTPError;
  +signUpError?: HTTPError;
  +redirectTo?: string;
  +handleOpenSentForgotPasswordModal: () => void;
};

type State = {
  password: {
    value: string;
    errors?: string[];
  };
};

const messages = defineMessages({
  passwordPlaceholder: {
    id: 'auth.password.placeholder',
  },
});


export default class AlreadyExistsMailAccount extends Component<Props, State> {
  formValidator: FormValidator;
  userEmail: ?string;

  state = {
    password: {
      value: '',
    },
  };

  constructor(props: Props) {
    super(props);
    this.formValidator = new FormValidator({
      password: {
        required: 'required',
      }
    });
  }

  componentDidUpdate(prevProps: Props) {
    const { authError, signUpError } = this.props;
    if (authError && prevProps.authError !== authError) {
      if (authError.code === 401 && authError.data &&
        authError.data.name === 'AuthenticationFailedError') {
        this.setState({
          password: {
            ...this.state.password,
            errors: (this.state.password.errors || []).concat('wrongCredentials'),
          }
        });
      }
      if (authError.code === 401 && authError.data &&
        authError.data.name === 'UserExistsWithMailError') {
        this.userEmail = authError.data.email;
      }
    } else if (signUpError && prevProps.signUpError !== signUpError) {
      if (signUpError.code === 400 && signUpError.data &&
        signUpError.data.name === 'UserAlreadyExistsError') {
        this.userEmail = signUpError.data.email;
      }
    }
  }

  handleChange(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      this.setState({ [name]: { value } });
    }
  }

  handleFocus(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      this.setState({ [name]: { value } });
    }
  }

  handleBlur(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      const errors = this.formValidator.validateField(name, value);
      if (errors.length && (errors[0])) {
        this.setState({ [name]: { value } });
      }
    }
  }

  handleClickForgotPassword(e: Event) {
    e.preventDefault();
    const { handleOpenSentForgotPasswordModal } = this.props;
    handleOpenSentForgotPasswordModal();
  }

  handleSubmit(e: Event) {
    e.preventDefault();
    const { login, redirectTo, authError } = this.props;
    const passwordValue = this.state.password.value;
    const errors = this.formValidator.validateForm({
      password: passwordValue,
    });
    if (errors.length) {
      errors.forEach((error: FormValidationResult) => {
        this.setState({
          [error.field]: {
            ...this.state[error.field],
            errors: error.errors,
          },
        });
      });
    } else if (this.userEmail) {
      login(this.userEmail, passwordValue, redirectTo);
    }
  }

  render() {
    const { isOpen, isAuthing, authError, handleClose } = this.props;
    const { password } = this.state;
    const passwordError = password.errors && FormValidator.buildErrorLabel(password.errors, {
      required: 'auth.password.error.required',
    });
    let error;
    if (authError) {
      if (authError.code === 401 && authError.data && authError.data.name === 'AuthenticationFailedError') {
        error = <FormattedMessage id="auth.error.wrongCredentials">{
          txt => (<p className={classNames(style.red, style.lowerError)}>{txt}</p>)
        }</FormattedMessage>;
      } else if (authError.code === 429) {
        error = <FormattedMessage id="httpErrors.post.429">{
          txt => (<p className={classNames(style.red, style.lowerError)}>{txt}</p>)
        }</FormattedMessage>;
      } else if (Math.round(authError.code / 100) === 5) {
        error = <FormattedMessage id="auth.error.httpError">{
          txt => (<p className={classNames(style.red, style.lowerError)}>{txt}</p>)
        }</FormattedMessage>;
      }
    }
    return (
      <Modal open={isOpen}
             handleClose={handleClose}
             width={544}
             height={312}>
        {isAuthing ? <Loading type="sk-three-bounce"
                              className={style.modalLoading}
                              number={3}/> :
          <section className={style.alreadyExistsMailAccount}>
            <form className={classNames(style.authForm, formStyle.formWrap)}
                  onSubmit={this.handleSubmit.bind(this)}>
              <FormattedMessage id="modal.alreadyExistMainAcount.title">{txt => <h1>{txt}</h1>}</FormattedMessage>
              {this.userEmail &&
              <p className={style.email}>{this.userEmail}</p>
              }
              <section className={classNames(formStyle.row, formStyle.inputRow)}>
                <FormattedMessage id="modal.alreadyExistMainAcount.label">{
                  txt => (<label className={formStyle.label}>{txt}</label>)
                }</FormattedMessage>
                <input type="password"
                       className={classNames(inputStyle.input,
                         { [inputStyle.red]: password.errors && password.errors.length },
                         { [inputStyle.lightBlue]: !!password.value })}
                       onChange={this.handleChange.bind(this)}
                       onFocus={this.handleFocus.bind(this)}
                       disabled={isAuthing}
                       name="password"
                       placeholder={this.context.intl.formatMessage(messages.passwordPlaceholder)}/>
                {passwordError}
              </section>
              {error}
              <FormattedMessage id="auth.button">{
                txt => (
                  <button
                    type="submit"
                    disabled={isAuthing}
                    className={classNames(buttonStyle.btn, buttonStyle.lightBlue, style.submitBtn)}
                    onClick={this.handleSubmit.bind(this)}>{txt}
                  </button>
                )
              }</FormattedMessage>

              <FormattedMessage id="auth.forgotPassword">{
                txt => (<a onClick={this.handleClickForgotPassword.bind(this)} className={style.separated}>{txt}</a>)
              }</FormattedMessage>
            </form>
          </section>
        }
      </Modal>
    )
  }
}

AlreadyExistsMailAccount.propTypes = {
  handleClose: PropTypes.func.isRequired,
  login: PropTypes.func.isRequired,
  isAuthing: PropTypes.bool,
  isOpen: PropTypes.bool,
  authError: PropTypes.object,
  signUpError: PropTypes.object,
  redirectTo: PropTypes.string,
  handleOpenSentForgotPasswordModal: PropTypes.func.isRequired,
};

AlreadyExistsMailAccount.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/auth/auth-modals/already-exists-mail-account/component/AlreadyExistsMailAccount.jsx